// /src/components/QRCodeManagement.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import api from '../api/axios';
import { 
  FaQrcode, 
  FaSpinner, 
  FaCopy, 
  FaPlus, 
  FaExclamationTriangle,
  FaTimes,
  FaLink,
  FaCalendarAlt,
  FaDownload,
  FaTrash,
  FaInfinity,
  FaBuilding
} from 'react-icons/fa';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const NoPropertiesCard = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 p-4">
      <FaBuilding className="text-gray-600 text-6xl mb-4" />
      <h2 className="text-xl font-semibold text-white mb-2">
        {t('qrcodeManagement.noProperties.title')}
      </h2>
      <p className="text-gray-400 text-center mb-4">
        {t('qrcodeManagement.noProperties.description')}
      </p>
      <Link
        to="/properties"
        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 
                 transition-colors duration-200 flex items-center"
      >
        <FaPlus className="mr-2" />
        {t('qrcodeManagement.noProperties.addProperty')}
      </Link>
    </div>
  );
};

const PropertySelector = ({ properties, selectedProperty, onSelect, isLoading }) => {
  const { t } = useTranslation();
  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardContent className="p-6">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {t('qrcodeManagement.propertySelector.label')}
        </label>
        {isLoading ? (
          <div className="flex items-center justify-center h-10">
            <FaSpinner className="animate-spin text-indigo-500" />
          </div>
        ) : (
          <select
            value={selectedProperty || ''}
            onChange={(e) => onSelect(e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                     text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          >
            <option value="">{t('qrcodeManagement.propertySelector.placeholder')}</option>
            {properties.map((property) => (
              <option key={property._id} value={property._id}>
                {property.name}
              </option>
            ))}
          </select>
        )}
      </CardContent>
    </Card>
  );
};

const GenerateForm = ({ onGenerate, onCancel, isGenerating }) => {
  const { t } = useTranslation();
  const [type, setType] = useState('qrcode');
  const [expiresAt, setExpiresAt] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onGenerate({ 
      type, 
      expiresAt: expiresAt || (type === 'link' ? addDays(new Date(), 7) : undefined) 
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">
            {t(`qrcodeManagement.generateForm.title.${type}`)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('qrcodeManagement.generateForm.accessType.label')}
              </label>
              <select
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  setExpiresAt('');
                }}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                         text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              >
                <option value="qrcode">{t('qrcodeManagement.generateForm.accessType.qrcode')}</option>
                <option value="link">{t('qrcodeManagement.generateForm.accessType.link')}</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t('qrcodeManagement.generateForm.expiresAt.label')} 
                {type === 'link' && t('qrcodeManagement.generateForm.expiresAt.linkMaxDays')}
              </label>
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                max={type === 'link' ? format(addDays(new Date(), 30), 'yyyy-MM-dd') : undefined}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                         text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
              <p className="mt-1 text-sm text-gray-400">
                {type === 'qrcode' ? 
                  t('qrcodeManagement.generateForm.expiresAt.qrcodeHint') : 
                  t('qrcodeManagement.generateForm.expiresAt.linkHint')}
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={isGenerating}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 
                         transition-colors disabled:opacity-50"
              >
                {t('qrcodeManagement.generateForm.buttons.cancel')}
              </button>
              <button
                type="submit"
                disabled={isGenerating}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 
                         transition-colors disabled:opacity-50 flex items-center"
              >
                {isGenerating ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    {t('qrcodeManagement.generateForm.buttons.generating')}
                  </>
                ) : (
                  <>
                    {type === 'qrcode' ? <FaQrcode className="mr-2" /> : <FaLink className="mr-2" />}
                    {t('qrcodeManagement.generateForm.buttons.generate')}
                  </>
                )}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const QRCodeCard = ({ qrCode, onCopyLink, onDelete }) => {
  const { t } = useTranslation();
  const expiryDate = qrCode.expiresAt ? new Date(qrCode.expiresAt) : null;
  const isExpired = expiryDate && expiryDate < new Date();
  const daysUntilExpiry = expiryDate ? 
    Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)) : 
    null;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = qrCode.cloudinaryUrl;
    link.download = `qr-code-${qrCode.shortId}.png`;
    link.click();
  };

  return (
    <Card className={`bg-gray-800 border-gray-700 ${
      isExpired ? 'opacity-60' : 'hover:border-indigo-500/50'
    } transition-all duration-300`}>
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-start md:space-x-6">
          {qrCode.type === 'qrcode' && qrCode.cloudinaryUrl && (
            <div className="mb-4 md:mb-0">
              <div className="bg-white p-4 rounded-lg w-40 h-40 flex items-center justify-center">
                <img
                  src={qrCode.cloudinaryUrl}
                  alt="QR Code"
                  className="w-full h-full"
                />
              </div>
              <button
                onClick={handleDownload}
                className="mt-2 w-full px-3 py-1.5 bg-indigo-600 text-white rounded-lg 
                         hover:bg-indigo-700 transition-colors duration-200 flex items-center justify-center"
              >
                <FaDownload className="mr-2" /> {t('qrcodeManagement.accessCodes.card.download')}
              </button>
            </div>
          )}

          <div className="flex-1 space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  {qrCode.type === 'qrcode' ? (
                    <>
                      <FaQrcode className="mr-2 text-indigo-400" />
                      {t('qrcodeManagement.accessCodes.card.qrcodeAccess')}
                    </>
                  ) : (
                    <>
                      <FaLink className="mr-2 text-indigo-400" />
                      {t('qrcodeManagement.accessCodes.card.linkAccess')}
                    </>
                  )}
                </h3>
                {isExpired ? (
                  <span className="px-2 py-1 bg-red-500/10 text-red-500 text-sm rounded-full">
                    {t('qrcodeManagement.accessCodes.status.expired')}
                  </span>
                ) : expiryDate ? (
                  <span className="px-2 py-1 bg-green-500/10 text-green-400 text-sm rounded-full">
                    {t('qrcodeManagement.accessCodes.status.active')}
                  </span>
                ) : (
                  <span className="px-2 py-1 bg-indigo-500/10 text-indigo-400 text-sm rounded-full flex items-center">
                    <FaInfinity className="mr-1" /> {t('qrcodeManagement.accessCodes.status.unlimited')}
                  </span>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between bg-gray-700/50 p-3 rounded-lg">
                  <div className="flex items-center text-gray-300">
                    <FaLink className="mr-2" />
                    <span className="text-sm truncate">
                      {`${window.location.origin}/guest-chat/${qrCode.shortId}`}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onCopyLink(qrCode._id)}
                      className="p-1.5 text-indigo-400 hover:text-indigo-300 
                               hover:bg-gray-600 rounded transition-colors"
                      title={t('qrcodeManagement.accessCodes.card.copyLink')}
                    >
                      <FaCopy />
                    </button>
                    <button
                      onClick={() => onDelete(qrCode._id)}
                      className="p-1.5 text-red-500 hover:text-red-700 
                               hover:bg-gray-600 rounded transition-colors"
                      title={t('qrcodeManagement.accessCodes.card.deleteAccess')}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
                {expiryDate && (
                  <div className="flex items-center text-gray-400">
                    <FaCalendarAlt className="mr-2" />
                    <span className="text-sm">
                      {isExpired 
                        ? t('qrcodeManagement.accessCodes.card.expiryInfo.expired', { days: Math.abs(daysUntilExpiry) })
                        : t('qrcodeManagement.accessCodes.card.expiryInfo.expires', { days: daysUntilExpiry })
                      }
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const QRCodeManagement = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [qrCodes, setQRCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [newQRCode, setNewQRCode] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchProperties = async () => {
      setIsLoading(true);
      try {
        const response = await api.get('/property/user/current', {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        });
        const allProperties = [...response.data.owned, ...response.data.managed];
        setProperties(allProperties);
        
        // If we have properties and propertyId is provided, select it
        if (allProperties.length > 0) {
          if (propertyId) {
            const property = allProperties.find(p => p._id === propertyId);
            if (property) {
              setSelectedProperty(propertyId);
            }
          } else if (!selectedProperty) {
            setSelectedProperty(allProperties[0]._id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch properties:', error);
        setError(t('qrcodeManagement.messages.error.loadProperties'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperties();
  }, [propertyId, selectedProperty, t]);

  useEffect(() => {
    if (selectedProperty) {
      fetchQRCodes();
    }
  }, [selectedProperty]);

  const fetchQRCodes = async () => {
    try {
      const response = await api.get(`/qrcode/${selectedProperty}`, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setQRCodes(response.data);
    } catch (error) {
      console.error('Failed to fetch access codes:', error);
      setError(t('qrcodeManagement.messages.error.loadAccess'));
    }
  };

  const handlePropertySelect = (id) => {
    setSelectedProperty(id);
    navigate(`/qrcodes/${id}`);
    setNewQRCode(null);
  };

  const handleGenerate = async (formData) => {
    if (!selectedProperty) return;
    
    setIsGenerating(true);
    setError(null);
    try {
      const response = await api.post(
        '/qrcode/generate',
        { 
          propertyId: selectedProperty,
          type: formData.type,
          expiresAt: formData.expiresAt
        },
        { headers: { 'x-auth-token': localStorage.getItem('token') } }
      );
      setNewQRCode(response.data);
      await fetchQRCodes();
      setShowGenerateForm(false);
      toast.success(t(`qrcodeManagement.messages.generateSuccess.${formData.type}`));
    } catch (error) {
      console.error('Failed to generate access:', error);
      toast.error(error.response?.data?.error || t('qrcodeManagement.messages.error.generate'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDelete = async (accessCodeId) => {
    toast(
      t('qrcodeManagement.messages.deleteConfirm'),
      {
        action: {
          label: t('common.actions.delete'),
          onClick: async () => {
            try {
              await api.delete(`/qrcode/${accessCodeId}`, {
                headers: { 'x-auth-token': localStorage.getItem('token') }
              });
              setQRCodes(prevCodes => prevCodes.filter(code => code._id !== accessCodeId));
              toast.success(t('qrcodeManagement.messages.deleteSuccess'));
            } catch (error) {
              console.error('Error deleting access code:', error);
              toast.error(error.response?.data?.error || t('qrcodeManagement.messages.error.delete'));
            }
          },
        },
        cancel: {
          label: t('common.actions.cancel'),
          onClick: () => toast.dismiss(),
        },
        duration: Infinity,
      }
    );
  };

  const handleCopyLink = (id) => {
    const accessCode = qrCodes.find(code => code._id === id) || newQRCode;
    if (!accessCode) return;

    const link = `${window.location.origin}/guest-chat/${accessCode.shortId}`;
    navigator.clipboard.writeText(link);
    toast.success(t('qrcodeManagement.messages.copySuccess'));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <FaSpinner className="animate-spin text-indigo-500 text-4xl" />
      </div>
    );
  }

  if (!isLoading && properties.length === 0) {
    return <NoPropertiesCard />;
  }

  return (
    <div className="flex-1 space-y-8 p-8 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white">
            {t('qrcodeManagement.title')}
          </h2>
          <p className="mt-2 text-gray-400">
            {t('qrcodeManagement.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowGenerateForm(true)}
          disabled={!selectedProperty || isGenerating}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 
                   transition-colors duration-200 flex items-center disabled:opacity-50 
                   disabled:cursor-not-allowed"
        >
          <FaQrcode className="mr-2" />
          {t('qrcodeManagement.generateAccess')}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-center">
          <FaExclamationTriangle className="text-red-500 mr-2" />
          <span className="text-red-500">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-400"
          >
            <FaTimes />
          </button>
        </div>
      )}

      {/* Property Selector */}
      <PropertySelector
        properties={properties}
        selectedProperty={selectedProperty}
        onSelect={handlePropertySelect}
        isLoading={isLoading}
      />

      {/* Generate Form Modal */}
      {showGenerateForm && (
        <GenerateForm
          onGenerate={handleGenerate}
          onCancel={() => setShowGenerateForm(false)}
          isGenerating={isGenerating}
        />
      )}

      {/* Access Codes Grid */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-white">
            {t('qrcodeManagement.accessCodes.title')}
          </h3>
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-400 rounded-full mr-1"></div>
              <span>{t('qrcodeManagement.accessCodes.status.active')}</span>
            </div>
            <div className="flex items-center ml-4">
              <div className="w-2 h-2 bg-red-400 rounded-full mr-1"></div>
              <span>{t('qrcodeManagement.accessCodes.status.expired')}</span>
            </div>
            <div className="flex items-center ml-4">
              <div className="w-2 h-2 bg-indigo-400 rounded-full mr-1"></div>
              <span>{t('qrcodeManagement.accessCodes.status.unlimited')}</span>
            </div>
          </div>
        </div>
        
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <FaSpinner className="animate-spin text-indigo-500 text-2xl" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* New Access Code */}
            {newQRCode && (
              <div className="relative">
                <div className="absolute -left-2 top-0 bottom-0 w-1 bg-indigo-500 rounded-full" />
                <QRCodeCard
                  qrCode={newQRCode}
                  onCopyLink={handleCopyLink}
                  onDelete={handleDelete}
                />
              </div>
            )}

            {/* Existing Access Codes */}
            {qrCodes.length === 0 ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6 text-center">
                  <FaQrcode className="mx-auto text-4xl text-gray-600 mb-4" />
                  <p className="text-gray-400">
                    {t('qrcodeManagement.accessCodes.noAccess.title')}
                  </p>
                  <button
                    onClick={() => setShowGenerateForm(true)}
                    className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg 
                             hover:bg-indigo-700 transition-colors duration-200 
                             inline-flex items-center"
                  >
                    <FaPlus className="mr-2" />
                    {t('qrcodeManagement.accessCodes.noAccess.generateFirst')}
                  </button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {qrCodes.map((qrCode) => (
                  <QRCodeCard
                    key={qrCode._id}
                    qrCode={qrCode}
                    onCopyLink={handleCopyLink}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QRCodeManagement;